import {
  CURRENCIES,
  CURRENCY_SIGNS,
  createRequestSchema,
  type FileItem,
  REQUEST_TYPE_LABELS,
  REQUEST_TYPES,
  type RequestListItem,
  type RequestType,
  requestStatusLabel,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Banknote, ChevronLeft, FileOutput, Plane, Plus, Receipt, Search } from 'lucide-react';
import { type ComponentType, type FormEvent, useRef, useState } from 'react';
import { Dialog } from '@/components/dialog';
import { FileUpload } from '@/components/file-upload';
import { DateRange } from '@/components/list-tools';
import { EntityStatus, ProcessHint } from '@/components/process';
import {
  Button,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  Segmented,
  Select,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { formatMoment, formatMoney, todayIso } from '@/lib/format';
import { assigneesQuery, requestsQuery } from '@/lib/queries';

export interface RequestsSearch {
  scope?: 'all';
  type?: RequestType;
  state?: 'open' | 'closed';
  q?: string;
  author?: string;
  from?: string;
  to?: string;
}

export const REQUEST_ICONS: Record<RequestType, ComponentType<{ className?: string }>> = {
  business_trip: Plane,
  invoice: Receipt,
  cash: Banknote,
  outgoing: FileOutput,
};

const REQUEST_HINTS: Record<RequestType, string> = {
  business_trip: 'Согласование у руководителей, оформление бухгалтерией, отчётные документы после поездки',
  invoice: 'Согласование оплаты, оплата бухгалтерией, закрывающие документы',
  cash: 'Согласование, выдача денег, отчёт о расходах с чеками',
  outgoing: 'Регистрация письма или документа в журнале исходящих',
};

export function RequestsPage() {
  const search = useSearch({ strict: false }) as RequestsSearch;
  const navigate = useNavigate();
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState(search.q ?? '');
  const debounce = useRef<number | undefined>(undefined);
  const scope = search.scope ?? 'mine';
  const people = useQuery({ ...assigneesQuery, enabled: scope === 'all' });
  const list = useQuery(
    requestsQuery({
      scope,
      type: search.type,
      state: search.state,
      q: search.q,
      userId: scope === 'all' ? search.author : undefined,
      createdFrom: search.from,
      createdTo: search.to,
    }),
  );

  const setSearch = (patch: Partial<RequestsSearch>) =>
    navigate({ to: '/requests', search: { ...search, ...patch }, replace: true });
  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setSearch({ q: value.trim() || undefined }), 250);
  };

  return (
    <div>
      <PageHeader
        title="Заявки"
        description="Командировки, счета на оплату, выдача наличных и исходящие документы — по маршрутам согласования."
        actions={
          can('requests.request.create') && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Новая заявка
            </Button>
          )
        }
      />
      {creating && <NewRequestDialog onClose={() => setCreating(false)} />}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented
          label="Чьи заявки"
          value={scope}
          options={[
            { value: 'mine', label: 'Мои' },
            { value: 'all', label: 'Все доступные' },
          ]}
          onChange={(v) => setSearch({ scope: v === 'all' ? 'all' : undefined })}
        />
        <Select
          aria-label="Вид заявки"
          value={search.type ?? ''}
          onChange={(e) => setSearch({ type: (e.target.value as RequestType) || undefined })}
          className="w-full sm:w-[200px]"
        >
          <option value="">Все виды</option>
          {REQUEST_TYPES.map((t) => (
            <option key={t} value={t}>
              {REQUEST_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Состояние"
          value={search.state ?? ''}
          onChange={(e) => setSearch({ state: (e.target.value as 'open' | 'closed') || undefined })}
          className="w-full sm:w-[170px]"
        >
          <option value="">Все</option>
          <option value="open">В работе</option>
          <option value="closed">Завершённые</option>
        </Select>
        <div className="relative w-full sm:max-w-[240px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Название или номер"
            aria-label="Поиск заявок"
            className="pl-9"
          />
        </div>
        {scope === 'all' && (
          <Select
            aria-label="Автор заявки"
            value={search.author ?? ''}
            onChange={(e) => setSearch({ author: e.target.value || undefined })}
            className="w-full sm:w-[220px]"
          >
            <option value="">Все сотрудники</option>
            {people.data?.map((p) => (
              <option key={p.userId} value={p.userId}>
                {p.fullName}
              </option>
            ))}
          </Select>
        )}
        <div className="w-full sm:w-[300px]">
          <DateRange
            label="Подана"
            from={search.from}
            to={search.to}
            onChange={(from, to) => setSearch({ from, to })}
          />
        </div>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <div className="panel px-6 py-10 text-center">
          <p className="font-medium">Заявок не найдено.</p>
          {can('requests.request.create') && (
            <button type="button" className="link mt-2 text-[13.5px]" onClick={() => setCreating(true)}>
              Подать заявку
            </button>
          )}
        </div>
      ) : (
        <RequestTable items={list.data.items} />
      )}
    </div>
  );
}

function RequestTable({ items }: { items: RequestListItem[] }) {
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[820px] text-left">
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            <th scope="col" className="w-14 px-4 py-2.5 font-medium">
              №
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Заявка
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Сумма
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Статус
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Автор
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Подана
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((r) => {
            const Icon = REQUEST_ICONS[r.type];
            return (
              <tr key={r.id} className="hover:bg-paper/70">
                <td className="px-4 py-2.5 tabular-nums text-steel">{r.number}</td>
                <td className="max-w-[420px] px-4 py-2.5">
                  <p className="flex items-center gap-1.5 text-[12.5px] text-steel">
                    <Icon className="size-3.5" aria-hidden />
                    {REQUEST_TYPE_LABELS[r.type]}
                  </p>
                  <Link to="/requests/$id" params={{ id: r.id }} className="font-medium hover:text-link">
                    {r.title}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                  {r.amount !== null ? (
                    formatMoney(r.amount, r.currency)
                  ) : (
                    <span className="text-steel">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <EntityStatus status={r.status} label={requestStatusLabel(r.type, r.status)} />
                  <div>
                    <ProcessHint summary={r.process} />
                  </div>
                </td>
                <td className="px-4 py-2.5">{r.author.fullName}</td>
                <td className="px-4 py-2.5 text-[13px] text-steel">{formatMoment(r.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Новая заявка ----------

function NewRequestDialog({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<RequestType | null>(null);
  return (
    <Dialog title={type ? REQUEST_TYPE_LABELS[type] : 'Новая заявка'} onClose={onClose} width={600}>
      {type ? (
        <RequestForm type={type} onBack={() => setType(null)} onClose={onClose} />
      ) : (
        <ul className="grid gap-2 px-5 py-4 sm:grid-cols-2">
          {REQUEST_TYPES.map((t) => {
            const Icon = REQUEST_ICONS[t];
            return (
              <li key={t}>
                <button
                  type="button"
                  onClick={() => setType(t)}
                  className="flex h-full w-full flex-col items-start gap-1 rounded-[var(--radius-control)] border border-line-strong px-4 py-3 text-left hover:border-graphite hover:bg-paper"
                >
                  <span className="flex items-center gap-2 font-semibold">
                    <Icon className="size-4.5 text-ink-soft" aria-hidden />
                    {REQUEST_TYPE_LABELS[t]}
                  </span>
                  <span className="text-[13px] text-steel">{REQUEST_HINTS[t]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

function RequestForm({
  type,
  onBack,
  onClose,
}: {
  type: RequestType;
  onBack: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const today = todayIso();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: unknown) => api<{ id: string }>('/requests', { method: 'POST', body }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['requests'] });
      onClose();
      await navigate({ to: '/requests/$id', params: { id: res.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(err.message);
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    for (const [k, v] of fd.entries()) if (typeof v === 'string' && v.trim()) data[k] = v.trim();
    // «48 500,50» → 48500.50
    if (data.amount) data.amount = data.amount.replace(/[\s\u00a0]/g, '').replace(',', '.');
    const body = { type, data, fileIds: files.map((f) => f.id) };
    const parsed = createRequestSchema.safeParse(body);
    setFormError(null);
    if (!parsed.success) {
      const f: Record<string, string> = {};
      for (const i of parsed.error.issues) f[i.path.join('.')] ??= i.message;
      setErrors(f);
      return;
    }
    setErrors({});
    save.mutate(body);
  }

  const err = (k: string) => errors[`data.${k}`];
  const fileLabel: Record<RequestType, string> = {
    business_trip: 'Файлы (приглашение, программа)',
    invoice: 'Копия счёта *',
    cash: 'Файлы',
    outgoing: 'Файл документа',
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-4 px-5 py-4">
        {type === 'business_trip' && (
          <>
            <Field label="Куда *" error={err('destination')}>
              {(p) => <Input {...p} name="destination" maxLength={300} placeholder="Город, организация" />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Начало *" error={err('startDate')}>
                {(p) => <Input {...p} type="date" name="startDate" min={today} />}
              </Field>
              <Field label="Окончание *" error={err('endDate')}>
                {(p) => <Input {...p} type="date" name="endDate" min={today} />}
              </Field>
            </div>
            <Field label="Цель поездки *" error={err('purpose')}>
              {(p) => <Textarea {...p} name="purpose" maxLength={5000} />}
            </Field>
          </>
        )}
        {type === 'invoice' && (
          <>
            <Field label="За что платим *" error={err('purpose')}>
              {(p) => <Input {...p} name="purpose" maxLength={1000} />}
            </Field>
            <Field label="Поставщик" error={err('supplier')}>
              {(p) => <Input {...p} name="supplier" maxLength={300} />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Номер счёта" error={err('invoiceNumber')}>
                {(p) => <Input {...p} name="invoiceNumber" maxLength={100} />}
              </Field>
              <Field label="Дата счёта" error={err('invoiceDate')}>
                {(p) => <Input {...p} type="date" name="invoiceDate" />}
              </Field>
            </div>
            <div className="grid grid-cols-[1fr_110px] gap-4">
              <Field label="Сумма *" error={err('amount')}>
                {(p) => <Input {...p} name="amount" inputMode="decimal" placeholder="0,00" />}
              </Field>
              <Field label="Валюта" error={err('currency')}>
                {(p) => (
                  <Select {...p} name="currency" defaultValue="RUB">
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c} {CURRENCY_SIGNS[c]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          </>
        )}
        {type === 'cash' && (
          <>
            <Field label="На что нужны деньги *" error={err('purpose')}>
              {(p) => <Textarea {...p} name="purpose" maxLength={5000} />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Сумма, ₽ *" error={err('amount')}>
                {(p) => <Input {...p} name="amount" inputMode="decimal" placeholder="0,00" />}
              </Field>
              <Field label="Нужны к дате" error={err('neededBy')}>
                {(p) => <Input {...p} type="date" name="neededBy" min={today} />}
              </Field>
            </div>
          </>
        )}
        {type === 'outgoing' && (
          <>
            <Field label="Кому *" error={err('recipient')}>
              {(p) => <Input {...p} name="recipient" maxLength={300} placeholder="Организация или человек" />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Номер документа" error={err('documentNumber')}>
                {(p) => <Input {...p} name="documentNumber" maxLength={100} />}
              </Field>
              <Field label="Дата документа *" error={err('documentDate')}>
                {(p) => <Input {...p} type="date" name="documentDate" defaultValue={today} />}
              </Field>
            </div>
            <Field label="О документе *" error={err('about')}>
              {(p) => <Textarea {...p} name="about" maxLength={5000} />}
            </Field>
          </>
        )}
        <Field label={fileLabel[type]} error={errors.fileIds}>
          {(p) => <FileUpload id={p.id} invalid={p['aria-invalid']} value={files} onChange={setFiles} />}
        </Field>
        {formError && !Object.keys(errors).length && (
          <p role="alert" className="text-[13px] text-danger">
            {formError}
          </p>
        )}
      </div>
      <div className="flex flex-wrap justify-between gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft className="size-4" aria-hidden />
          Другой вид
        </Button>
        <Button type="submit" loading={save.isPending}>
          Отправить
        </Button>
      </div>
    </form>
  );
}
